import React, { useState, useCallback, useEffect } from 'react';
import {
    Layout, Table, Button, Space, Input, Form, message, Typography,
    Popconfirm, Select, Drawer, Row, Col, Divider, InputNumber, Tag, Modal
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, FolderOutlined
} from '@ant-design/icons';
import BoundAlgorithmList from './BoundAlgorithmList';

const { Sider, Content } = Layout;
const { Text, Title } = Typography;
const { Option } = Select;

// ============================================================
// Mock 数据 — TODO: 联调时替换为接口数据
// ============================================================

// 服务分类列表（模拟 GET /package/service/category/list）
const MOCK_CATEGORIES = [
    { id: 1, name: '云端存储', isDefault: 0, sort: 1, serviceCount: 2 },
    { id: 2, name: 'AI识别', isDefault: 0, sort: 2, serviceCount: 2 },
    { id: 3, name: '通信服务', isDefault: 0, sort: 3, serviceCount: 1 },
    { id: 0, name: '未分类', isDefault: 1, sort: 0, serviceCount: 1 },
];

// 服务列表（模拟 GET /package/service/listAll）
const INIT_SERVICES = [
    { id: 1, serviceName: '全天云存', serviceId: '300010', descr: '7×24小时全天候录像存储', categoryId: 1, categoryName: '云端存储', updatedAt: Math.floor(Date.now() / 1000) - 86400 * 5, aiiotTypeConfig: '[{"AlgorithmType":1010,"AIIoTName":"云端录像","Config":{}}]' },
    { id: 2, serviceName: '事件云存', serviceId: '300011', descr: '由设备触发的事件录像存储', categoryId: 1, categoryName: '云端存储', updatedAt: Math.floor(Date.now() / 1000) - 86400 * 3, aiiotTypeConfig: '[]' },
    { id: 3, serviceName: '逗留提醒', serviceId: '300036', descr: '检测人员在区域内逗留超时并提醒', categoryId: 2, categoryName: 'AI识别', updatedAt: Math.floor(Date.now() / 1000) - 86400 * 10, aiiotTypeConfig: '[]' },
    { id: 4, serviceName: '人脸识别', serviceId: '300033', descr: '端侧或云端人脸识别比对服务', categoryId: 2, categoryName: 'AI识别', updatedAt: Math.floor(Date.now() / 1000) - 86400 * 2, aiiotTypeConfig: '[]' },
    { id: 5, serviceName: '短信服务', serviceId: '300031', descr: '设备告警短信通知服务', categoryId: 3, categoryName: '通信服务', updatedAt: Math.floor(Date.now() / 1000) - 86400 * 15, aiiotTypeConfig: '[]' },
    { id: 6, serviceName: 'AI家庭看护', serviceId: '400001', descr: '云端多模态AI智能看护服务', categoryId: 0, categoryName: '未分类', updatedAt: Math.floor(Date.now() / 1000), aiiotTypeConfig: '[]' },
];

// ============================================================
// 主组件
// ============================================================
const PackageManage = () => {
    // ---- 数据 ----
    const [categories, setCategories] = useState(MOCK_CATEGORIES);
    const [services, setServices] = useState(INIT_SERVICES);
    const [activeCategoryId, setActiveCategoryId] = useState(null);
    const [loading] = useState(false);

    // ---- 表格选择 ----
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    // ---- Drawer（对齐 Real 项目 ServiceDialog，物模型配置已移除）----
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [drawerSubmitting, setDrawerSubmitting] = useState(false);
    const [serviceForm] = Form.useForm();

    // ---- 分类 Modal ----
    const [catModalVisible, setCatModalVisible] = useState(false);
    const [editingCat, setEditingCat] = useState(null);
    const [catForm] = Form.useForm();

    // ---- 批量移动 Modal ----
    const [batchMoveVisible, setBatchMoveVisible] = useState(false);
    const [targetCategoryId, setTargetCategoryId] = useState(null);

    // ---- 初始化 ----
    useEffect(() => {
        const defaultCat = categories.find(c => c.isDefault === 1) || categories[0];
        if (defaultCat) setActiveCategoryId(defaultCat.id);
        // eslint-disable-next-line
    }, []);

    // 当前分类下的服务
    const filteredServices = activeCategoryId
        ? services.filter(s => s.categoryId === activeCategoryId)
        : services;

    // 批量移动目标分类选项（排除当前分类）
    const batchMoveOptions = categories.filter(c => c.id !== activeCategoryId);

    // ============================================================
    // 分类操作
    // ============================================================
    const handleAddCat = () => {
        setEditingCat(null);
        catForm.resetFields();
        catForm.setFieldsValue({ sort: 0 });
        setCatModalVisible(true);
    };

    const handleEditCat = (cat) => {
        setEditingCat(cat);
        catForm.setFieldsValue({ name: cat.name, sort: cat.sort || 0 });
        setCatModalVisible(true);
    };

    const handleCatSave = async () => {
        try {
            const values = await catForm.validateFields();
            if (editingCat) {
                setCategories(prev => prev.map(c => c.id === editingCat.id ? { ...c, name: values.name, sort: values.sort } : c));
                message.success('编辑成功');
            } else {
                const newId = Math.max(...categories.map(c => c.id), 0) + 1;
                setCategories(prev => [...prev, { id: newId, name: values.name, isDefault: 0, sort: values.sort, serviceCount: 0 }]);
                message.success('创建成功');
            }
            setCatModalVisible(false);
        } catch { /* 校验失败 */ }
    };

    // ============================================================
    // 服务 Drawer（对齐 Real 项目 ServiceDialog）
    // ============================================================
    const handleOpenDrawer = () => {
        // 模拟 loadCategories（Real 项目异步拉取）
        const isEdit = !!editingService;
        if (isEdit) {
            const record = editingService;
            serviceForm.setFieldsValue({
                serviceName: record.serviceName,
                serviceId: record.serviceId,
                descr: record.descr || '',
                categoryId: record.categoryId,
            });
        } else {
            serviceForm.resetFields();
            serviceForm.setFieldsValue({ categoryId: activeCategoryId });
        }
    };

    const handleDrawerClose = () => {
        serviceForm.resetFields();
        setEditingService(null);
        setDrawerSubmitting(false);
        setDrawerVisible(false);
    };

    const openCreateService = () => {
        if (activeCategoryId === null) return;
        setEditingService(null);
        setDrawerSubmitting(false);
        setDrawerVisible(true);
    };

    const openEditService = (record) => {
        setEditingService(record);
        setDrawerSubmitting(false);
        setDrawerVisible(true);
    };

    const handleServiceSubmit = async () => {
        try {
            const values = await serviceForm.validateFields();
            setDrawerSubmitting(true);

            // 模拟异步保存（Real 项目：addPackageServiceList / updatePackageServiceList）
            await new Promise(r => setTimeout(r, 400));

            const now = Math.floor(Date.now() / 1000);

            if (editingService) {
                setServices(prev => prev.map(s =>
                    s.id === editingService.id
                        ? {
                            ...s,
                            serviceName: values.serviceName,
                            descr: values.descr || '',
                            categoryId: values.categoryId,
                            categoryName: categories.find(c => c.id === values.categoryId)?.name || '',
                            updatedAt: now,
                            // 编辑时保留已有的 aiiotTypeConfig（算法库侧维护）
                        }
                        : s
                ));
                message.success('修改成功');
            } else {
                const newId = Math.max(...services.map(s => s.id), 0) + 1;
                setServices(prev => [...prev, {
                    id: newId,
                    serviceName: values.serviceName,
                    serviceId: values.serviceId,
                    descr: values.descr || '',
                    categoryId: values.categoryId,
                    categoryName: categories.find(c => c.id === values.categoryId)?.name || '',
                    updatedAt: now,
                    aiiotTypeConfig: '[]',   // 新增时初始化为空
                }]);
                message.success('创建成功');
            }
            refreshCategoryCounts();
            handleDrawerClose();
        } catch {
            // 表单校验失败不做处理
        } finally {
            setDrawerSubmitting(false);
        }
    };

    const handleDeleteService = (record) => {
        setServices(prev => prev.filter(s => s.id !== record.id));
        setSelectedRowKeys(prev => prev.filter(k => k !== record.id));
        refreshCategoryCounts();
        message.success('删除成功');
    };

    const refreshCategoryCounts = useCallback(() => {
        setCategories(prev => prev.map(c => ({
            ...c,
            serviceCount: services.filter(s => s.categoryId === c.id).length,
        })));
    }, [services]);

    // ============================================================
    // 批量移动
    // ============================================================
    const handleBatchMove = () => {
        setTargetCategoryId(null);
        setBatchMoveVisible(true);
    };

    const handleBatchMoveSubmit = () => {
        if (!targetCategoryId) {
            message.warning('请选择目标服务分类');
            return;
        }
        const targetCat = categories.find(c => c.id === targetCategoryId);
        setServices(prev => prev.map(s =>
            selectedRowKeys.includes(s.id)
                ? { ...s, categoryId: targetCategoryId, categoryName: targetCat?.name || '' }
                : s
        ));
        message.success('批量移动成功');
        setBatchMoveVisible(false);
        setSelectedRowKeys([]);
        refreshCategoryCounts();
    };

    // ============================================================
    // 表格列
    // ============================================================
    const columns = [
        { title: '服务名称', dataIndex: 'serviceName', width: 160, render: t => <Text strong>{t}</Text> },
        { title: '服务ID', dataIndex: 'serviceId', width: 140, render: t => <Text type="secondary">{t}</Text> },
        { title: '所属分类', dataIndex: 'categoryName', width: 120 },
        {
            title: '最后更新', dataIndex: 'updatedAt', width: 180,
            render: t => t ? new Date(t * 1000).toLocaleString('zh-CN') : '-',
        },
        {
            title: '操作', width: 160, align: 'center',
            render: (_, record) => (
                <Space size="small">
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditService(record)}>修改</Button>
                    <Popconfirm
                        title={`确定删除服务「${record.serviceName}」吗？`}
                        onConfirm={() => handleDeleteService(record)}
                        okText="确定" cancelText="取消"
                    >
                        <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const selectedCatName = categories.find(c => c.id === activeCategoryId)?.name || '全部';

    // ============================================================
    // 渲染
    // ============================================================
    return (
        <div style={{ height: '100%', background: '#fff' }}>
            <Layout style={{ minHeight: '100%', background: '#fff', padding: '20px' }}>
                {/* ═══ 左侧：服务分类 ═══ */}
                <Sider width={260} theme="light" style={{ borderRight: '1px solid #f0f0f0', marginRight: 20, background: '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 0 12px', borderBottom: '1px solid #f0f0f0', marginBottom: 8 }}>
                        <Title level={5} style={{ margin: 0, fontSize: 15 }}>服务分类</Title>
                        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAddCat}>新建</Button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {categories.map(cat => (
                            <div
                                key={cat.id}
                                onClick={() => { setActiveCategoryId(cat.id); setSelectedRowKeys([]); }}
                                style={{
                                    display: 'flex', alignItems: 'center', padding: '8px 10px',
                                    borderRadius: 4, cursor: 'pointer', fontSize: 14,
                                    background: activeCategoryId === cat.id ? '#ecf5ff' : 'transparent',
                                    color: activeCategoryId === cat.id ? '#1890ff' : '#303133',
                                    transition: 'background 0.15s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = activeCategoryId === cat.id ? '#ecf5ff' : '#f5f7fa'}
                                onMouseLeave={e => e.currentTarget.style.background = activeCategoryId === cat.id ? '#ecf5ff' : 'transparent'}
                            >
                                <FolderOutlined style={{ marginRight: 6, fontSize: 14 }} />
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat.name}</span>
                                {cat.isDefault === 1 && <Tag color="orange" style={{ marginLeft: 4, fontSize: 11, border: 'none', lineHeight: '18px' }}>默认</Tag>}
                                <span style={{ fontSize: 12, color: '#909399', marginLeft: 6, flexShrink: 0 }}>{cat.serviceCount}</span>
                                {cat.isDefault !== 1 && (
                                    <EditOutlined
                                        onClick={e => { e.stopPropagation(); handleEditCat(cat); }}
                                        style={{ fontSize: 12, color: '#1890ff', marginLeft: 8, flexShrink: 0 }}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </Sider>

                {/* ═══ 右侧：服务列表 ═══ */}
                <Content>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <Title level={5} style={{ margin: 0, fontSize: 15 }}>{selectedCatName} — 服务管理</Title>
                        <Space>
                            <Button
                                disabled={selectedRowKeys.length === 0}
                                onClick={handleBatchMove}
                            >
                                批量移动
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                disabled={activeCategoryId === null}
                                onClick={openCreateService}
                            >
                                新增服务
                            </Button>
                        </Space>
                    </div>

                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={filteredServices}
                        loading={loading}
                        size="small"
                        rowSelection={{
                            selectedRowKeys,
                            onChange: setSelectedRowKeys,
                        }}
                        locale={{ emptyText: '暂无服务' }}
                        pagination={false}
                    />
                </Content>
            </Layout>

            {/* ═══════════════════════════════════════ */}
            {/* 分类创建/编辑 Modal */}
            {/* ═══════════════════════════════════════ */}
            <Modal
                title={editingCat ? '编辑分类' : '新建分类'}
                open={catModalVisible}
                onCancel={() => setCatModalVisible(false)}
                onOk={handleCatSave}
                okText="确定"
                cancelText="取消"
                width={420}
                destroyOnClose
            >
                <Form form={catForm} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
                    <Form.Item name="name" label="分类名称" rules={[{ required: true, message: '请输入分类名称' }]}>
                        <Input placeholder="请输入分类名称" maxLength={64} />
                    </Form.Item>
                    <Form.Item name="sort" label="排序号">
                        <InputNumber min={0} max={9999} placeholder="排序号" style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* ═══════════════════════════════════════ */}
            {/* 批量移动 Modal */}
            {/* ═══════════════════════════════════════ */}
            <Modal
                title="批量移动服务"
                open={batchMoveVisible}
                onCancel={() => setBatchMoveVisible(false)}
                onOk={handleBatchMoveSubmit}
                okText="确定"
                cancelText="取消"
                width={450}
                destroyOnClose
            >
                <div style={{ marginBottom: 8, fontSize: 14, color: '#666' }}>已选服务数：{selectedRowKeys.length} 个服务</div>
                <div style={{ marginBottom: 8 }}>
                    <Text type="secondary">目标分类</Text>
                </div>
                <Select
                    value={targetCategoryId}
                    onChange={setTargetCategoryId}
                    placeholder="请选择目标服务分类"
                    style={{ width: '100%' }}
                >
                    {batchMoveOptions.map(cat => (
                        <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                    ))}
                </Select>
            </Modal>

            {/* ═══════════════════════════════════════ */}
            {/* 新建/编辑服务 Drawer（对齐 Real 项目 ServiceDialog） */}
            {/* ═══════════════════════════════════════ */}
            <Drawer
                title={editingService ? '修改服务类型' : '创建服务类型'}
                open={drawerVisible}
                onClose={handleDrawerClose}
                afterOpenChange={(open) => { if (open) handleOpenDrawer(); }}
                width={600}
                destroyOnClose
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button onClick={handleDrawerClose}>取消</Button>
                        <Button type="primary" loading={drawerSubmitting} onClick={handleServiceSubmit}>保存</Button>
                    </div>
                }
            >
                <Form form={serviceForm} layout="vertical" disabled={drawerSubmitting}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="serviceName" label="服务名称" rules={[{ required: true, message: '请输入服务名称' }]}>
                                <Input placeholder="例如：全天云存" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="serviceId" label="服务ID" rules={[{ required: true, message: '请输入服务ID' }]}>
                                <Input placeholder="例如：300010" disabled={!!editingService} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="descr" label="描述">
                        <Input.TextArea rows={3} placeholder="请输入服务描述" />
                    </Form.Item>

                    <Form.Item name="categoryId" label="所属分类">
                        <Select placeholder="请选择服务分类" allowClear style={{ width: '100%' }}>
                            {categories.map(cat => (
                                <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {/* 已绑定物模型（只读反显，编辑时可见） */}
                    {editingService && editingService.serviceId && (
                        <>
                            <Divider />
                            <div style={{ marginTop: 12 }}>
                                <Text strong style={{ fontSize: 14, color: '#606266' }}>
                                    已绑定物模型（{editingService.serviceId}）
                                </Text>
                                <BoundAlgorithmList aiiotTypeConfig={editingService.aiiotTypeConfig || '[]'} />
                            </div>
                        </>
                    )}
                </Form>
            </Drawer>
        </div>
    );
};

export default PackageManage;
